import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only Ration Card step detail for the view / review screens. Shows the
// stored, normalised outcome — no re-run form.
export function RationCardDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="Ration Card Verification" subtitle="Ration card & household result" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Canonical Ration Card Return-Data fields, in display order. The stored result is
// the NORMALISED, filtered object, so we render exactly the keys left enabled.
const RATION_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'ration_card_number', label: 'Ration Card Number' },
  { key: 'state', label: 'State' },
  { key: 'district', label: 'District' },
  { key: 'fps_id', label: 'Fair Price Shop ID' },
  { key: 'scheme_name', label: 'Scheme Name' },
  { key: 'onorc_allowed', label: 'ONORC Allowed' },
  { key: 'address', label: 'Address' },
  { key: 'head_of_family', label: 'Head of Family' },
  { key: 'member_count', label: 'Number of Members' },
  { key: 'members', label: 'Members' },
  { key: 'duplicate_uid_status', label: 'Duplicate UID Status' },
  { key: 'name_match_score', label: 'Name Match Score' },
  { key: 'name_matched_against', label: 'Name Matched Against' },
  { key: 'duplicate_ration_card_flag', label: 'Duplicate Ration Card Flag' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
];

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'name_match_score') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of RATION_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
